import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { MarkdownMessage } from '@/components/chat/MarkdownMessage';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CopyButton } from '@/components/ui/CopyButton';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';

/** The trial's "persona brief" deliverable, rendered from the stored persona. */
export function PersonaBriefPanel({ personaId }: { personaId: string }) {
  const q = useQuery({
    queryKey: ['persona', personaId, 'brief'],
    queryFn: () => api.getPersonaBrief(personaId),
  });

  if (q.isLoading) return <Skeleton className="h-96" />;
  if (q.isError || !q.data) {
    return <p className="text-sm text-destructive">Could not build the brief: {String(q.error)}</p>;
  }

  const md = q.data.markdown;
  const download = () => {
    const url = URL.createObjectURL(new Blob([md], { type: 'text/markdown' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `persona-brief-${personaId}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Built live from the voice sheet, claims rules and disclosure settings the pipeline uses.
        </p>
        <div className="flex items-center gap-2">
          <CopyButton value={md} label="Copy brief as markdown" />
          <Button size="sm" variant="outline" onClick={download}>
            <Download className="h-4 w-4" />
            Download .md
          </Button>
        </div>
      </div>
      <Card>
        <CardContent className="py-5">
          <MarkdownMessage text={md} />
        </CardContent>
      </Card>
    </div>
  );
}
